import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { useAdmin } from '../auth';
import { encodeQr, qrToSvg } from '../qr';
import { Btn, ErrorBox, Loading, fmtDate, useAction } from '../ui';

interface Status {
  enabled: boolean;
  enabledAt: string | null;
  required: boolean;
  backupCodesLeft: number;
}

/**
 * Двухфакторный вход (TOTP): Google Authenticator, Microsoft Authenticator, Aegis, 1Password и др.
 * Секрет показывается один раз при подключении; резервные коды — один раз после включения/перевыпуска.
 */
export function SecurityPage() {
  const { me, reload } = useAdmin();
  const action = useAction();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ['2fa'], queryFn: () => api<Status>('/admin/auth/2fa') });
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const done = () => {
    setCode('');
    setPassword('');
    void refetch();
    reload();
  };

  return (
    <div>
      <h1>Безопасность</h1>
      {me.twoFactor.required && !me.twoFactor.enabled ? (
        <p className="pill pill-warn">Владелец включил обязательный двухфакторный вход. Подключите его, чтобы продолжить работу.</p>
      ) : null}
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error || !data ? <ErrorBox error={error} onRetry={() => void refetch()} /> : (
        <section className="card form">
          <h2>Двухфакторный вход</h2>
          {codes ? (
            <div>
              <p><b>Резервные коды</b> — сохраните их в надёжном месте (менеджер паролей или распечатка). Каждый код
                действует один раз и заменяет код из приложения, если телефон потерян. Больше они показаны не будут.</p>
              <pre className="codes" data-testid="backup-codes">{codes.join('\n')}</pre>
              <Btn onClick={() => setCodes(null)}>Я сохранил коды</Btn>
            </div>
          ) : data.enabled ? (
            <div>
              <p>Включён {fmtDate(data.enabledAt)}. Неиспользованных резервных кодов: <b>{data.backupCodesLeft}</b>.</p>
              <p className="muted">Для перевыпуска кодов или отключения введите пароль и текущий код.</p>
              <label className="field">Пароль<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
              <label className="field">Код<input autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} /></label>
              <div className="row">
                <Btn busy={action.busy} disabled={!password || !code} onClick={() => void action.run(async () => {
                  const r = await api<{ backupCodes: string[] }>('/admin/auth/2fa/backup-codes', { method: 'POST', json: { password, code } });
                  setCodes(r.backupCodes);
                  done();
                })}>Новые резервные коды</Btn>
                <Btn busy={action.busy} disabled={!password || !code || data.required} onClick={() => void action.run(async () => {
                  if (!window.confirm('Отключить двухфакторный вход? Учётная запись станет защищена только паролем.')) return;
                  await api('/admin/auth/2fa/disable', { method: 'POST', json: { password, code } });
                  done();
                })}>Отключить</Btn>
              </div>
              {data.required ? <p className="muted small">Отключение недоступно: 2FA обязательна по настройкам площадки.</p> : null}
            </div>
          ) : setup ? (
            <div>
              <img
                className="qr"
                width={200}
                height={200}
                alt="QR-код для приложения-аутентификатора"
                data-testid="totp-qr"
                src={`data:image/svg+xml;utf8,${encodeURIComponent(qrToSvg(encodeQr(setup.otpauthUri, 'M')))}`}
              />
              <ol>
                <li>Отсканируйте QR-код приложением-аутентификатором или добавьте учётную запись вручную (или откройте ссылку на телефоне).</li>
                <li>Ключ: <code className="secret" data-testid="totp-secret">{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code></li>
                <li>Ссылка для телефона: <a href={setup.otpauthUri}>добавить в приложение</a></li>
                <li>Введите шестизначный код из приложения.</li>
              </ol>
              <label className="field">Код<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></label>
              <Btn busy={action.busy} disabled={code.length !== 6} onClick={() => void action.run(async () => {
                const r = await api<{ backupCodes: string[] }>('/admin/auth/2fa/enable', { method: 'POST', json: { code } });
                setSetup(null);
                setCodes(r.backupCodes);
                done();
              })}>Включить</Btn>
            </div>
          ) : (
            <div>
              <p>Не подключён. При входе, кроме пароля, будет нужен код из приложения на телефоне — украденного пароля
                станет недостаточно для входа в панель.</p>
              <Btn busy={action.busy} onClick={() => void action.run(async () => setSetup(await api('/admin/auth/2fa/setup', { method: 'POST' })))}>
                Подключить
              </Btn>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
